import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { InfoPage } from '@/components/info-page';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { lessonTypeaheadSearch, useLessonById } from '@/lib/api-call-hooks';
import { EmptyArray, getAdminSessions, getUser } from '@/lib/api-calls';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { useErrorToast } from '@/lib/error-handling';
import { usePaginatedFilters } from '@/lib/filtering';
import { classTimeTypesFilterOption } from '@/lib/form-utils';
import { paginationRequestSchema } from '@/lib/pagination';
import { CancelSessions } from '@/routes/_layout/sessions/-components/cancel-sessions';
import { RestoreSessions } from '@/routes/_layout/sessions/-components/restore-sessions';
import { SessionsTable } from '@/routes/_layout/sessions/-components/sessions-table';
import { endOfDay, FrequentTimeZones, isAirAdmin } from '@airreading/common';
import { AdminSession, classTimeTypeSchema } from '@airreading/common/data';
import { pluralize } from '@/lib/string-utils';
import { coachesTabs } from '../../-components/tabs';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker';
import { validateRouteRole } from '@/lib/routing';

export const Route = createFileRoute('/_layout/teachers/$teacherId/past-sessions/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: TeacherPastSessionsIndex,
  validateSearch: paginationRequestSchema.extend({
    query: z.string().optional(),
    lessons: z.string().array().optional(),
    classTypes: z.array(classTimeTypeSchema).optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
  }),
});

const endOfTodayInCST = endOfDay(Temporal.Now.instant(), FrequentTimeZones.UsCentral);

function TeacherPastSessionsIndex() {
  const queryClient = useQueryClient();
  const { teacherId } = Route.useParams();

  const { data: user } = useQuery({
    queryKey: ['user', teacherId],
    queryFn: () => getUser(teacherId),
  });
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      lessons: [[selectedLessons, activeSelectedLessons], setSelectedLessons],
      classTypes: [[selectedClassTypes, activeSelectedClassTypes], setSelectedClassTypes],
      startDate: [[selectedStartDate], setSelectedStartDate],
      endDate: [[selectedEndDate], setSelectedEndDate],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'lessons', 'classTypes', 'startDate', 'endDate']);

  const {
    data: sessionsResp,
    isPending,
    error,
    refetch,
  } = useQuery(
    getAdminSessions({
      pageIndex,
      pageSize,
      filters: {
        startOnOrAfter: selectedStartDate
          ? Temporal.PlainDate.from(selectedStartDate)
              .toZonedDateTime(FrequentTimeZones.UsCentral)
              .startOfDay()
              .toInstant()
              .toString()
          : undefined,
        startOnOrBefore: selectedEndDate
          ? Temporal.PlainDate.from(selectedEndDate)
              .toZonedDateTime(FrequentTimeZones.UsCentral)
              .add({ days: 1 })
              .startOfDay()
              .subtract({ milliseconds: 1 })
              .toInstant()
              .toString()
          : endOfTodayInCST.toString(),
        query: activeQuery,
        lessons: activeSelectedLessons,
        classTypes: activeSelectedClassTypes,
        coachId: teacherId,
      },
      sortBy: ['start-desc'],
    }),
  );

  useErrorToast(error, 'Error loading sessions. Please try again.');

  const sessions = useMemo(() => sessionsResp?.adminSessions || EmptyArray, [sessionsResp]);
  const rowCount = sessionsResp?.total || 0;

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<AdminSession>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => sessions.find((session) => session.id === id),
    });

  const bulkActionsMenuItems = (
    <>
      <CancelSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Cancel session', 'Cancel sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
      <RestoreSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Restore session', 'Restore sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
    </>
  );
  const fetchLessonById = useLessonById();

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search sessions by student, email, or teacher"
          value={query ?? ''}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="Lesson Name"
          options={lessonTypeaheadSearch(queryClient)}
          selectedValues={selectedLessons ?? []}
          onChange={setSelectedLessons}
          getOptionSearchFn={fetchLessonById}
        />
        <TableFilterDropdown
          title="Class Type"
          options={classTimeTypesFilterOption}
          selectedValues={selectedClassTypes ?? []}
          onChange={setSelectedClassTypes}
        />
        <TableFilterPlainDateRangePicker
          fromPlainDateString={selectedStartDate}
          toPlainDateString={selectedEndDate}
          onRangeSelect={(range) => {
            setSelectedStartDate(range.fromPlainDateString);
            setSelectedEndDate(range.toPlainDateString);
          }}
          placeholder="Date"
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      query,
      queryClient,
      selectedLessons,
      setSelectedLessons,
      fetchLessonById,
      selectedClassTypes,
      setSelectedClassTypes,
      selectedStartDate,
      selectedEndDate,
      resetFilters,
      setQuery,
      setSelectedStartDate,
      setSelectedEndDate,
    ],
  );

  return (
    <InfoPage
      headerText={user?.fullNameOrEmail || 'Past Sessions'}
      tabs={coachesTabs({ teacherId })}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Teachers',
            link: {
              to: '/teachers',
            },
          },
        ],
        currentPage: user?.fullNameOrEmail || 'Past Sessions',
      }}
    >
      <SessionsTable
        sessions={sessions}
        hasError={!!error}
        isLoading={isPending}
        errorState={<TableErrorState refetchAction={refetch} />}
        tableHookProps={{
          rowCount,
          state: {
            pagination: {
              pageIndex,
              pageSize,
            },
            rowSelection,
          },
          onRowSelectionChange,
          getRowId,
          onPaginationChange,
        }}
        displayTimeZone={FrequentTimeZones.UsCentral}
        filters={{ filterLeftComponents }}
        bulkActionsMenuItems={bulkActionsMenuItems}
        from="teacher-past"
      />
    </InfoPage>
  );
}
